import type { Workflow, WorkflowTemplate } from '../../contracts/routines.types';

/**
 * Adapt a marketplace preset into the Workflow shape the Explore split cards /
 * conversation chrome already expect. The catalog id is used as ``workflow_id``
 * so selection stays a plain ``kind: 'workflow'`` id — detail panes resolve it
 * from the presets cache when ``GET /workflows/:id`` has nothing.
 *
 * Marker: ``source_template_id === workflow_id`` means "this is a catalog card,
 * not a persisted workflow" — save paths should ``install`` rather than ``fork``.
 */
export function workflowFromPreset(template: WorkflowTemplate): Workflow {
  return {
    workflow_id: template.template_id,
    owner_user_id: null,
    creator_type: 'system',
    name: template.name,
    slug: template.template_id,
    description: template.description,
    instructions: template.instructions,
    source: template.source,
    source_recording_id: null,
    enabled: true,
    created_at: null,
    updated_at: null,
    requires_desktop: template.requires_desktop,
    forked_from_workflow_id: null,
    source_template_id: template.template_id,
    source_template_version: template.version,
    setup_values: {},
    shortcut: template.suggested_shortcut,
    category: template.category,
    icon: template.icon,
    highlights: template.highlights,
    suggested_cadence: template.suggested_cadence,
    suggested_event_types: template.suggested_event_types,
    setup_fields: template.setup_fields,
  };
}

/** True when the Workflow is a synthetic Explore card backed by a preset. */
export function isPresetCatalogWorkflow(workflow: Workflow): boolean {
  return (
    workflow.creator_type === 'system' &&
    workflow.source_template_id != null &&
    workflow.source_template_id === workflow.workflow_id
  );
}

/**
 * template_id → user workflow id for installed copies (``source_template_id``).
 * Also indexes ``forked_from_workflow_id`` as a fallback for older forks.
 */
export function installedByTemplateId(userWorkflows: Workflow[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const workflow of userWorkflows) {
    if (workflow.source_template_id) {
      map.set(workflow.source_template_id, workflow.workflow_id);
    }
  }
  for (const workflow of userWorkflows) {
    if (workflow.forked_from_workflow_id && !map.has(workflow.forked_from_workflow_id)) {
      map.set(workflow.forked_from_workflow_id, workflow.workflow_id);
    }
  }
  return map;
}
